import { filterByValidation, FinanceOptionProvider, FinanceOptionRecord } from "common/records/FinanceOption";
import { RootState } from "common/reducers";
import { calculatedFinancing } from "common/selectors/calculatedFinancing.selectors";
import { estimate } from "common/selectors/estimate.selectors";
import { estimateFinanceOptionIds } from "common/selectors/estimateFinanceOption.selectors";
import { ids } from "common/selectors/idsManagement.selectors";
import { currentJob } from "common/selectors/job.selectors";
import { Nullable } from "common/types";
import { List } from "immutable";
import { createSelector } from "reselect";

const rootKey = "financeOptions";

export const financeOptionsLoadedForOrgId = (state: RootState): Nullable<number> =>
  state.getIn([rootKey, "loadedForOrgId"]);

/**
 * Returns the byId from state
 * @param state The RootState
 * @returns the byId Map
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * Returns the financeOption record for the given id
 * @param state The RootState
 * @param props The financeOptionId
 * @returns the financeOption record
 */
export const financeOption = (state: RootState, props: { financeOptionId: number }) =>
  byId(state).get(props.financeOptionId);

/**
 * Returns the ids from ids management
 * @param state The RootState
 * @returns the ids List
 */
export const financeOptionsIds = (state: RootState) => ids(state, { modelName: rootKey });

/**
 * Returns the list financeOption records for the ids in ids management
 * @param state The RootState
 * @returns the financeOptions List
 */
export const financeOptions = createSelector([byId, financeOptionsIds], (byId, financeOptionsIds) => {
  return financeOptionsIds.map((id) => byId.get(id as number)).filter((d) => d) as List<FinanceOptionRecord>;
});

const EMPTY_FINANCE_OPTIONS = List<FinanceOptionRecord>();

export const presentableFinanceOptions = createSelector(
  [financeOptions, estimate, (state: RootState) => currentJob(state)?.address?.state ?? ""],
  (financeOptions, linkedEstimate, jobState): List<FinanceOptionRecord> => {
    const statusFiltered = financeOptions.filter(
      (option) => option.status === "active" || option.status === "active_range",
    );
    if (statusFiltered.isEmpty()) {
      return EMPTY_FINANCE_OPTIONS;
    }
    return linkedEstimate ? filterByValidation(statusFiltered, linkedEstimate, jobState) : statusFiltered;
  },
);

export const rangeFinanceOptions = createSelector(
  [presentableFinanceOptions, estimateFinanceOptionIds],
  (presentable, selectedIds): List<FinanceOptionRecord> =>
    selectedIds.isEmpty() ? presentable : presentable.filter((option) => selectedIds.contains(option.id)),
);

export interface FilteredFinanceOptionsProps {
  financedAmount?: number;
  financeOptionIds: List<number>;
  forceRefresh?: boolean;
  providers: List<FinanceOptionProvider>;
}

/**
 * @param {RootState} state The RootState
 * @param {FilteredFinanceOptionsProps} props Parameters to use when identifying which finance options are available.
 * @returns {List<FinanceOptionRecord>} A list of safe finance options
 */
export const filteredFinanceOptions = createSelector(
  [financeOptions, (state: RootState, props: FilteredFinanceOptionsProps) => ({ props, state })],
  (financeOptions, stateProps) => {
    const { props, state } = stateProps;
    const { financedAmount, forceRefresh, providers } = props;

    let safeFinanceOptions = financeOptions.filter((fo) => !!fo);
    if (providers) {
      safeFinanceOptions = safeFinanceOptions.filter((fo) => providers.includes(fo.provider));
    }

    if (!forceRefresh && financedAmount) {
      // financing already validly calculated
      safeFinanceOptions = safeFinanceOptions.filter((fo) => {
        return !calculatedFinancing(state, { financedAmount, financeOptionId: fo.id });
      });
    }

    return safeFinanceOptions;
  },
);
